import { useMemo, useState } from 'react';
import { formatINR } from '@/utils/formatters';
import type { VendorOption } from './vendorOptions';

export type SupplierLink = { vendorId: string; name: string; gstin: string | null; rate: number | null; unit: string | null; isPreferred: boolean };

type Props = {
  vendors: VendorOption[];
  suppliers: SupplierLink[];
  value: string;
  onChange: (vendorId: string, supplier: SupplierLink | null) => void;
  label?: string;
};

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

/** SUN-239: searchable vendor dropdown (name, legal name or GSTIN); suppliers of the material first. */
export function VendorPicker({ vendors, suppliers, value, onChange, label = 'Vendor' }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const supplierById = useMemo(() => new Map(suppliers.map((s) => [s.vendorId, s])), [suppliers]);

  const matches = (v: { name: string; legalName?: string | null; gstin?: string | null }) => {
    const q = query.trim().toLowerCase();
    return !q || [v.name, v.legalName, v.gstin].some((t) => (t || '').toLowerCase().includes(q));
  };
  const supplying = useMemo(
    () => vendors.filter((v) => supplierById.has(v.id) && matches(v))
      .sort((a, b) => Number(supplierById.get(b.id)!.isPreferred) - Number(supplierById.get(a.id)!.isPreferred) || byName(a, b)),
    [vendors, supplierById, query], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const others = useMemo(
    () => vendors.filter((v) => !supplierById.has(v.id) && matches(v)).sort(byName),
    [vendors, supplierById, query], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const chosen = vendors.find((v) => v.id === value);
  const chosenText = chosen ? `${supplierById.get(chosen.id)?.isPreferred ? '★ ' : ''}${chosen.name}${chosen.gstin ? ` · ${chosen.gstin}` : ''}` : '';

  const pick = (v: VendorOption) => {
    onChange(v.id, supplierById.get(v.id) ?? null);
    setOpen(false);
    setQuery('');
  };
  const row = (v: VendorOption) => {
    const s = supplierById.get(v.id);
    return (
      <li key={v.id} role="option" aria-selected={v.id === value} tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()} onClick={() => pick(v)}
        className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-gray-600">
        <span className="min-w-0">
          <span className="text-gray-900 dark:text-white">{s?.isPreferred ? '★ ' : ''}{v.name}</span>
          {v.legalName && v.legalName.trim().toUpperCase() !== v.name.trim().toUpperCase() && (
            <span className="block text-xs text-gray-500">{v.legalName}</span>
          )}
          {v.gstin && <span className="block font-mono text-xs text-gray-400">{v.gstin}</span>}
        </span>
        {s && s.rate != null && <span className="shrink-0 text-xs text-gray-600 dark:text-gray-300">{formatINR(s.rate)}{s.unit ? ` / ${s.unit}` : ''}</span>}
      </li>
    );
  };

  return (
    <div className="relative">
      <input
        type="text" role="combobox" aria-label={label} aria-expanded={open} aria-autocomplete="list"
        value={open ? query : chosenText}
        placeholder="Search vendor by name or GSTIN"
        onFocus={() => setOpen(true)}
        onBlur={() => { setOpen(false); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      />
      {open && (
        <ul role="listbox" className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700">
          {supplying.length > 0 && <li role="presentation" className="bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-500 dark:bg-gray-800">Supplies this material</li>}
          {supplying.map(row)}
          {others.length > 0 && <li role="presentation" className="bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-500 dark:bg-gray-800">All vendors</li>}
          {others.map(row)}
          {!supplying.length && !others.length && <li role="presentation" className="px-3 py-2 text-sm text-gray-500">No vendor found</li>}
        </ul>
      )}
    </div>
  );
}
